import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../styles/addReceipt.module.css";
import { InvoiceCxc } from "./InvoiceCxc";
import { CxcSummary } from "./CxcSummary";
import { formatCurrency } from "../../../../../shared/utils/formatCurrency";

const PAGE_SIZE = 10;

export function CxcDetails({
  setFacturasData,
  facturasData,
  updateAbono,
  updateChecked,
  toggleDiferencial,
  acumularSaldosFavor,
  restante,
  count,
  onDistribute,
  receiptMonto,
  bancoMoneda = "CRC",
  TC_compra   = 1,
}) {
  return (
    <div className={styles["cxc-details"]}>
      <CxcDetailsHeader />
      <CxcDetailsBody
        setFacturasData={setFacturasData}
        facturasData={facturasData}
        count={count}
        updateAbono={updateAbono}
        updateChecked={updateChecked}
        toggleDiferencial={toggleDiferencial}
        acumularSaldosFavor={acumularSaldosFavor}
        onDistribute={onDistribute}
        receiptMonto={receiptMonto}
        restante={restante}
        bancoMoneda={bancoMoneda}
        TC_compra={TC_compra}
      >
        <CxcSummary
          facturasData={facturasData}
          restante={restante}
          bancoMoneda={bancoMoneda}
          TC_compra={TC_compra}
        />
      </CxcDetailsBody>
    </div>
  );
}

function CxcDetailsHeader() {
  const { t } = useTranslation();
  return (
    <div className={styles["cxc-details-header"]}>
      <h1>
        <i className={`fa-solid fa-folder ${styles["icon-cxc-details"]}`} />{" "}
        {t("recibos.registroCxC")}
      </h1>
    </div>
  );
}

function CxcDetailsBody({
  setFacturasData,
  facturasData,
  count,
  updateAbono,
  updateChecked,
  toggleDiferencial,
  acumularSaldosFavor,
  onDistribute,
  receiptMonto,
  restante,
  bancoMoneda = "CRC",
  TC_compra   = 1,
  children,
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [acumularOpen, setAcumularOpen] = useState(false);
  const totalPages = Math.ceil(facturasData.length / PAGE_SIZE);

  const visibles = facturasData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // CxC con saldo a favor (saldo < 0) actualmente marcadas
  const negativasChecked = facturasData.filter(
    (f) => f.checked && Number(f.saldo) < 0,
  );
  const puedeAcumular =
    typeof acumularSaldosFavor === "function" && negativasChecked.length >= 2;

  function handleSelectAll(checked) {
    if (checked) {
      onDistribute();
    } else {
      setFacturasData((prev) =>
        prev.map((inv) => ({ ...inv, abono: 0, favor: 0, checked: false })),
      );
    }
    setPage(1);
  }

  const someChecked = Boolean(facturasData.find((inv) => inv.checked));

  return (
    <div className={styles["receipt-details-body"]}>
      <div className={styles["table-responsive"]}>
        <div className={styles["invoice-table"]}>
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={someChecked}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th>{t("recibos.colFactura")}</th>
                <th>{t("recibos.colRubro")}</th>
                <th>{t("common.saldo")}</th>
                <th style={{ paddingRight: "20px" }}>{t("cxp.abono")}</th>
                <th style={{ paddingLeft: 6 }}></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((inv) => (
                <InvoiceCxc
                  key={inv.id}
                  inv={inv}
                  updateAbono={updateAbono}
                  updateChecked={updateChecked}
                  toggleDiferencial={toggleDiferencial}
                  remainingCRC={restante}
                  TC_compra={TC_compra}
                  bancoMoneda={bancoMoneda}
                />
              ))}
            </tbody>
          </table>

          <div
            className={styles["footer-info"]}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 14px",
              minWidth: 720,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span>{facturasData.length} {t("recibos.cxcActivas")}</span>
              {puedeAcumular && (
                <button
                  type="button"
                  onClick={() => setAcumularOpen(true)}
                  style={{
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    background: "rgba(74,222,128,0.12)",
                    border: "1px solid rgba(74,222,128,0.4)",
                    borderRadius: 6,
                    color: "#4ade80",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  <i className="fa-solid fa-layer-group" style={{ marginRight: 6 }} />
                  {t("recibos.acumularSaldos")}
                </button>
              )}
            </div>

            {totalPages > 1 && (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  style={{
                    padding: "3px 10px",
                    fontSize: 12,
                    background: page === 1 ? "var(--hover-bg)" : "var(--hover-bg-strong)",
                    color: page === 1 ? "var(--text-muted)" : "var(--text-secondary)",
                    border: "1px solid var(--border-card)",
                    borderRadius: 4,
                    cursor: page === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  {t("common.anteriorCorto")}
                </button>
                <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  {page} / {totalPages}
                </span>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  style={{
                    padding: "3px 10px",
                    fontSize: 12,
                    background: page === totalPages ? "var(--hover-bg)" : "var(--hover-bg-strong)",
                    color: page === totalPages ? "var(--text-muted)" : "var(--text-secondary)",
                    border: "1px solid var(--border-card)",
                    borderRadius: 4,
                    cursor: page === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  {t("common.siguienteCorto")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {children}

      {acumularOpen && (
        <AcumularSaldosDialog
          negativas={negativasChecked}
          bancoMoneda={bancoMoneda}
          acumularSaldosFavor={acumularSaldosFavor}
          onClose={() => setAcumularOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * AcumularSaldosDialog
 *
 * Diálogo para consolidar varios saldos a favor (CxC con saldo < 0) en una
 * sola factura. El usuario elige la factura destino; las demás quedan saldadas.
 */
function AcumularSaldosDialog({ negativas, acumularSaldosFavor, onClose }) {
  const { t } = useTranslation();
  const [targetId, setTargetId] = useState(negativas[0]?.id ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // El saldo a favor se muestra como valor absoluto en la moneda de la factura
  const fmtFavor = (inv) =>
    inv.moneda === "USD"
      ? `$${Math.abs(Number(inv.saldo)).toFixed(2)}`
      : `₡${formatCurrency(Math.abs(Number(inv.saldo)))}`;

  // Total combinado por moneda (no se mezclan monedas en el backend)
  const totalCRC = negativas
    .filter((i) => i.moneda !== "USD")
    .reduce((a, i) => a + Math.abs(Number(i.saldo)), 0);
  const totalUSD = negativas
    .filter((i) => i.moneda === "USD")
    .reduce((a, i) => a + Math.abs(Number(i.saldo)), 0);

  async function handleConfirm() {
    if (!targetId) return;
    setSaving(true);
    setError("");
    try {
      const sourceIds = negativas
        .filter((i) => i.id !== targetId)
        .map((i) => i.id);
      await acumularSaldosFavor(targetId, sourceIds);
      onClose();
    } catch (e) {
      setError(e.message || t("recibos.acumularError"));
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-card)",
          borderRadius: 12,
          padding: 24,
          width: "min(480px, 92vw)",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              background: "rgba(74,222,128,0.15)",
              borderRadius: 8,
              padding: "8px 10px",
              color: "#4ade80",
            }}
          >
            <i className="fa-solid fa-layer-group" />
          </div>
          <h3
            style={{
              margin: 0,
              color: "var(--text-primary)",
              fontSize: "1rem",
              fontWeight: 700,
            }}
          >
            {t("recibos.acumularTitulo")}
          </h3>
        </div>

        <p
          style={{
            color: "var(--text-secondary)",
            fontSize: "0.85rem",
            marginBottom: 12,
          }}
        >
          {t("recibos.acumularElegirDestino")}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {negativas.map((inv) => (
            <label
              key={inv.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                border: `1px solid ${
                  targetId === inv.id
                    ? "rgba(74,222,128,0.5)"
                    : "var(--border-card)"
                }`,
                background:
                  targetId === inv.id ? "rgba(74,222,128,0.08)" : "transparent",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="target-cxc"
                checked={targetId === inv.id}
                onChange={() => setTargetId(inv.id)}
              />
              <span style={{ flex: 1, color: "var(--text-primary)", fontSize: "0.88rem" }}>
                {inv.factura?.numero_factura ?? "—"}
                <span
                  style={{
                    display: "block",
                    fontSize: "0.72rem",
                    color: "var(--text-muted)",
                  }}
                >
                  {inv.factura?.cuenta_ingreso?.nombre ?? ""}
                </span>
              </span>
              <span style={{ color: "#fb923c", fontWeight: 600, fontSize: "0.85rem" }}>
                {fmtFavor(inv)}
              </span>
            </label>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 16,
            paddingTop: 14,
            borderTop: "1px solid var(--border-card)",
          }}
        >
          <span style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>
            {t("recibos.acumularTotalCombinado")}
          </span>
          <span style={{ color: "#4ade80", fontWeight: 700 }}>
            {totalCRC > 0 && `₡${formatCurrency(totalCRC)}`}
            {totalCRC > 0 && totalUSD > 0 && "  ·  "}
            {totalUSD > 0 && `$${totalUSD.toFixed(2)}`}
          </span>
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.3)",
              borderRadius: 7,
              padding: "8px 12px",
              color: "#f87171",
              fontSize: "0.82rem",
            }}
          >
            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 18,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: "8px 16px",
              background: "var(--hover-bg)",
              border: "1px solid var(--border-card)",
              borderRadius: 7,
              color: "var(--text-secondary)",
              fontWeight: 600,
              fontSize: "0.85rem",
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {t("recibos.acumularCancelar")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving || !targetId}
            style={{
              padding: "8px 16px",
              background: saving ? "rgba(74,222,128,0.5)" : "#22c55e",
              border: "none",
              borderRadius: 7,
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: saving || !targetId ? "not-allowed" : "pointer",
            }}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                &nbsp;…
              </>
            ) : (
              <>
                <i className="fa-solid fa-layer-group" style={{ marginRight: 6 }} />
                {t("recibos.acumularConfirmar")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
